import { useQuery } from "@tanstack/react-query";
import { isLostStage, isWonDeal, LOST_STAGES, DEAL_WON_STAGE } from "@/lib/constants/deal-stages";
import type { DealStage } from "@/lib/types/database/deals";
import { useMemo } from "react";
import { createClient } from "@wtc/ui";
import { useUniversalAuth } from "@wtc/ui";
import { logger } from "@/lib/logger";
import { SalesPerformanceMetrics, Deal } from "@/lib/types/database/deals";
import { validateDealRevenue } from "@/lib/utils/crm/deal-data-validation";
import { fetchAllSupabaseRows } from "@/lib/hooks/api/dashboard/paginate";

// ============================================================================
// CEO SALES PERFORMANCE METRICS HOOK
// ============================================================================
//
// PURPOSE: Calculate sales performance metrics for CEO dashboard
//
// METRICS:
// - Win Rate: (Count of deals with stage='won') / (Count of all deals) × 100
// - Average Contract Value (ACV): Sum of revenue for won deals / Count of won deals
// - Conversion Ratio: (Count of won deals) / (Count of communications tied to the
//   period's deal companies — i.e. deal-related outreach) × 100
//
// ============================================================================

interface UseCeoSalesPerformanceMetricsOptions {
  dateFrom?: string; // ISO date
  dateTo?: string; // ISO date
  enabled?: boolean;
}

interface CommunicationCount {
  total: number;
}

export function useCeoSalesPerformanceMetrics(
  options: UseCeoSalesPerformanceMetricsOptions = {}
) {
  const { isAuthenticated } = useUniversalAuth();
  const supabase = createClient();
  const { enabled = true, dateFrom, dateTo } = options;

  // Validate date range
  if (dateFrom && dateTo) {
    const from = new Date(dateFrom);
    const to = new Date(dateTo);
    if (from > to) {
      throw new Error("Start date must be before end date");
    }
  }

  // Fetch all deals within the date range
  // Note: We filter by close_date (when deals close) not created_at (when deals are created)
  // This ensures consistent metrics across all dashboard sections
  const { data: deals, isLoading: dealsLoading, error: dealsError } = useQuery({
    queryKey: ["ceo-sales-performance-deals", dateFrom, dateTo],
    queryFn: async (): Promise<Deal[]> => {
      if (!isAuthenticated) {
        throw new Error("Authentication required");
      }

      // Page to completion: PostgREST caps a single response near 1000 rows, which
      // would silently truncate win-rate / ACV math past that. fetchAllSupabaseRows
      // loops .range() until a short page. The builder MUST be constructed fresh per
      // call (the Supabase query builder is single-use).
      try {
        return await fetchAllSupabaseRows<Deal>(({ from, to }) => {
          let query = supabase.from("deals").select("*");

          // Apply date filtering on close_date for consistency with forecast metrics
          if (dateFrom) {
            query = query.gte("close_date", dateFrom);
          }
          if (dateTo) {
            query = query.lte("close_date", dateTo);
          }

          return query.order("created_at", { ascending: false }).range(from, to);
        });
      } catch (error) {
        logger.error("Error fetching deals", error);
        throw error;
      }
    },
    enabled: isAuthenticated && enabled,
    staleTime: 1 * 60 * 1000, // 1 minute for critical sales metrics
  });

  // The set of company IDs that own a deal closing in this period. The
  // conversion-ratio denominator is scoped to outreach (communications) tied to
  // these companies — "deal-related outreach" — not every communication logged.
  const dealCompanyIds = useMemo<string[]>(() => {
    if (!deals) return [];
    const ids = new Set<string>();
    for (const deal of deals) {
      if (typeof deal.company_id === "string" && deal.company_id.length > 0) {
        ids.add(deal.company_id);
      }
    }
    return Array.from(ids);
  }, [deals]);

  // Fetch the count of communications tied to the period's deal companies.
  // We filter by occurred_at (activity during the period) AND company_id IN the
  // period's deal companies, so the denominator is deal-related outreach. Depends
  // on the deals query so the company set is known before counting.
  const {
    data: communicationCount,
    isLoading: communicationsLoading,
    error: communicationsError,
  } = useQuery({
    queryKey: [
      "ceo-sales-performance-communications",
      dateFrom,
      dateTo,
      dealCompanyIds,
    ],
    queryFn: async (): Promise<number> => {
      if (!isAuthenticated) {
        throw new Error("Authentication required");
      }

      // No deal companies in the period → no deal-related outreach to count.
      if (dealCompanyIds.length === 0) {
        return 0;
      }

      let query = supabase
        .from("communications")
        .select("id", { count: "exact", head: true })
        // Scope to outreach tied to the period's deal companies. Communications
        // with a null company_id are naturally excluded by this filter.
        .in("company_id", dealCompanyIds);

      // Apply date filtering on occurred_at for communications
      // This captures communications that happened during the period
      if (dateFrom) {
        query = query.gte("occurred_at", dateFrom);
      }
      if (dateTo) {
        query = query.lte("occurred_at", dateTo);
      }

      const { count, error } = await query;

      if (error) {
        logger.error("Error fetching communications count", error);
        throw error;
      }

      return count || 0;
    },
    // Wait for deals before counting so the company scope is known.
    enabled: isAuthenticated && enabled && deals !== undefined,
    staleTime: 1 * 60 * 1000, // 1 minute for critical sales metrics
  });

  // Calculate metrics using useMemo to prevent unnecessary recalculations
  const metrics = useMemo((): SalesPerformanceMetrics => {
    if (!deals) {
      return {
        winRate: 0,
        averageContractValue: 0,
        conversionRatio: 0,
        totalDeals: 0,
        wonDeals: 0,
        lostDeals: 0,
        totalCommunications: communicationCount || 0,
        periodStart: dateFrom || new Date().toISOString().split("T")[0],
        periodEnd: dateTo || new Date().toISOString().split("T")[0],
      };
    }

    // Count deals by status
    const totalDeals = deals.length;
    const wonDeals = deals.filter((deal) => isWonDeal(deal.stage)).length;
    const lostDeals = deals.filter((deal) => isLostStage(deal.stage)).length;

    // Calculate win rate: (won deals / total deals) × 100
    const winRate = totalDeals > 0 ? (wonDeals / totalDeals) * 100 : 0;

    // Calculate ACV: sum of revenue for won deals / count of won deals
    // Only include deals with valid revenue
    const wonDealsRevenue = deals
      .filter((deal) => isWonDeal(deal.stage))
      .reduce((sum, deal) => {
        const { isValid, value: revenue } = validateDealRevenue(deal.revenue);
        if (!isValid) return sum;
        return sum + revenue;
      }, 0);
    const averageContractValue = wonDeals > 0 ? wonDealsRevenue / wonDeals : 0;

    // Calculate conversion ratio: (won deals / deal-related communications) × 100.
    // The denominator (communicationCount) is already scoped at query level to
    // communications whose company_id is one of the period's deal companies.
    const dealRelatedCommunications = communicationCount || 0;
    const conversionRatio =
      dealRelatedCommunications > 0 ? (wonDeals / dealRelatedCommunications) * 100 : 0;

    return {
      winRate: Math.round(winRate * 100) / 100,
      averageContractValue: Math.round(averageContractValue * 100) / 100,
      conversionRatio: Math.round(conversionRatio * 100) / 100,
      totalDeals,
      wonDeals,
      lostDeals,
      totalCommunications: dealRelatedCommunications,
      periodStart: dateFrom || new Date().toISOString().split("T")[0],
      periodEnd: dateTo || new Date().toISOString().split("T")[0],
    };
  }, [deals, communicationCount, dateFrom, dateTo]);

  return {
    metrics,
    isLoading: dealsLoading || communicationsLoading,
    error: dealsError || communicationsError,
    deals,
    communicationCount,
  };
}
